import { graphql } from '../../gql';
import { useModalContext } from '../../modal/ModalContext';
import { ModalActions } from '../../modal/components/ModalActions';
import { ModalContent } from '../../modal/components/ModalContent';
import { useApiMutation } from '../../query/hooks';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from '../../store/snackbar';
import { z } from 'zod';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';

const deleteMetricMutation = graphql(`
	mutation connections_deleteMetric($contentMetricId: UUID!) {
		deleteContentMetric(by: { id: $contentMetricId }) {
			ok
			errorMessage
			node {
				metric {
					dataSet {
						id
						source {
							type
						}
					}
				}
			}
		}
	}
`);

const deleteMetricModalSchema = z.object({
	contentMetricId: z.string(),
	name: z.string(),
});

const DeleteMetricModal: FC<z.infer<typeof deleteMetricModalSchema>> = ({ contentMetricId, name }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { closeModal } = useModalContext();

	const { mutate: deleteMetric } = useApiMutation(deleteMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.deleteContentMetric.ok,
				openSnackbar,
				toastMessages: {
					success: intl.formatMessage({ id: 'connections.metrics.success.delete' }),
					error: intl.formatMessage({ id: 'connections.metrics.error.delete' }),
				},
				callback: () => {
					if (!result.deleteContentMetric.ok) {
						console.error(result.deleteContentMetric.errorMessage);
					}
				},
			});
		},
	});

	return (
		<>
			<ModalContent
				isDelete
				title={intl.formatMessage({ id: 'connections.metrics.delete-modal.title' })}
				description={intl.formatMessage({ id: 'connections.metrics.delete-modal.description' }, { name })}
			/>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deleteMetric({ contentMetricId });
					closeModal();
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
			/>
		</>
	);
};

export { deleteMetricModalSchema, DeleteMetricModal };
